/**
 * Hero ostidagi ochiq statistika paneli (ЧТЗ 4.2.11.1 — infografika).
 *
 * To'rtta asosiy ko'rsatkich o'z grafigi bilan:
 *   — o'rmon fondi: maydon + respublika hududidagi ulushi (100 katakli setka);
 *   — ekilgan ko'chatlar: viloyatlar kesimida gorizontal ustunlar;
 *   — yong'inlar: joriy yil oylari bo'yicha ustunlar (eng ko'p oy belgilanadi);
 *   — murojaatlar: hal etilgan / jarayondagi ulush (ikki qismli shkala).
 * Qolgan chop etilgan ko'rsatkichlar ostidagi ixcham qatorda turadi — panel
 * butun ochiq statistikani ko'rsatadi.
 *
 * Grafiklar oddiy HTML elementlar: animatsiya CSS'da, bo'lim ko'rinish
 * maydoniga kirganda (`data-in`) boshlanadi. Har bir grafikning ekran
 * o'quvchi uchun jadval ko'rinishi bor (ЧТЗ 4.2.12 — ma'lumot faqat grafikda
 * berilmaydi), qiymatlar esa to'g'ridan-to'g'ri yozuvlar yoki tooltip'da.
 */
import { useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, ReactNode } from "react";

import type { PublicStatisticsCharts } from "@/shared/api/types";
import type { StatisticItem } from "@/shared/data/landing";
import { useI18n } from "@/shared/i18n";
import { CountUp } from "@/shared/ui/CountUp";
import { Icon } from "@/shared/ui/Icon";

import { formatStat } from "./publicData";

const LOCALES: Record<string, string> = { ru: "ru", en: "en" };
/** Grafikli kartochkalar (shu tartibda). Qolgan ko'rsatkichlar — ixcham qatorda. */
const FOREST = "forest-fund-area";
const SHARE = "forest-fund-share";
const TREES = "trees-planted-year";
const FIRES = "fires-year";
const APPEALS = "appeals-resolved";
const CHARTED = new Set([FOREST, SHARE, TREES, FIRES, APPEALS]);
/** Viloyatlar grafigida shuncha qator; qolganlari «Boshqalar»ga yig'iladi. */
const REGION_ROWS = 4;
/** Sanoq birliklari: raqam yonida ortiqcha («14 ta ko'chatxonalar»). */
const COUNT_UNITS = new Set(["ta", "dona", "шт", "шт.", "ед.", "pcs", "pc"]);
/** Qisqartmani fuqaroga tushunarli shaklga keltirish. */
const UNIT_LONG: Record<string, Record<string, string>> = { uz: { ga: "gektar" } };

function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ""));
}

/** Qiymat va birlik: «%» raqamga yopishtiriladi, sanoq birliklari tushiriladi. */
function splitValue(item: StatisticItem, lang: string): { value: string; unit: string } {
  const value = formatStat(item.value, lang);
  const unit = item.unit.trim();
  if (unit === "%") return { value: `${value}%`, unit: "" };
  if (COUNT_UNITS.has(unit.toLowerCase())) return { value, unit: "" };
  return { value, unit: UNIT_LONG[lang]?.[unit] ?? unit };
}

/** «Andijon viloyati» → «Andijon», «Андижанская область» → «Андижанская». */
function shortRegion(name: string): string {
  return name
    .replace(/\s+(viloyati|Respublikasi|область|Region|Province)$/i, "")
    .replace(/^Республика\s+/i, "")
    .replace(/\s+shahri$/i, " sh.")
    .trim();
}

/**
 * O'zbekcha oy nomlari qo'lda: ko'p brauzerlarning `Intl` ma'lumotida `uz`
 * yo'q va «M10» kabi zaxira chiqadi. Rus/ingliz tillari uchun `Intl` yetarli.
 */
const UZ_MONTHS_SHORT = ["Yan", "Fev", "Mar", "Apr", "May", "Iyn", "Iyl", "Avg", "Sen", "Okt", "Noy", "Dek"];

function monthNames(lang: string): string[] {
  if (lang === "uz") return UZ_MONTHS_SHORT;
  const format = new Intl.DateTimeFormat(LOCALES[lang] ?? lang, { month: "short" });
  return Array.from({ length: 12 }, (_, month) => {
    const name = format.format(new Date(2026, month, 1)).replace(".", "");
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
}

const vars = (values: Record<string, string | number>) => values as CSSProperties;

/* ------------------------------------------------------------- tooltip */

interface Tip {
  text: string;
  x: number;
  y: number;
}

type TipHandler = (target: HTMLElement | null, text?: string) => void;

/* -------------------------------------------------------------- qismlar */

function Figure({ item, lang, icon }: { item: StatisticItem; lang: string; icon?: ReactNode }) {
  const { value, unit } = splitValue(item, lang);
  return (
    <header className="sp-head">
      <h3 className="sp-label">
        {icon}
        {item.label}
      </h3>
      <p className="sp-value">
        {item.value === null ? "—" : <CountUp className="sp-num" value={value} />}
        {unit ? <small>{unit}</small> : null}
      </p>
    </header>
  );
}

/** 100 katak — ulushning har bir foizi bitta katak (hudud parchalari metaforasi). */
function Waffle({ share, lang }: { share: StatisticItem; lang: string }) {
  const { t } = useI18n();
  const percent = Math.max(0, Math.min(100, ((share.value ?? 0) / (share.max || 100)) * 100));
  const lit = Math.round(percent);
  const caption = fill(t("stat.shareOfCountry"), { v: splitValue(share, lang).value });
  return (
    <figure className="sp-chart">
      <div className="sp-waffle" role="img" aria-label={caption}>
        {Array.from({ length: 100 }, (_, index) => (
          <span key={index} className={index < lit ? "is-lit" : undefined} style={vars({ "--i": index })} />
        ))}
      </div>
    </figure>
  );
}

function RegionBars({
  rows,
  lang,
  onTip,
}: {
  rows: PublicStatisticsCharts["trees_by_region"];
  lang: string;
  onTip: TipHandler;
}) {
  const { t } = useI18n();
  if (!rows?.length) return <p className="sp-empty">{t("stats.noData")}</p>;
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const head = rows.slice(0, REGION_ROWS).map((row) => ({ name: shortRegion(row.name), value: row.value }));
  const rest = rows.slice(REGION_ROWS).reduce((sum, row) => sum + row.value, 0);
  if (rest > 0) head.push({ name: t("stats.other"), value: rest });
  const max = Math.max(...head.map((row) => row.value), 1);
  const percent = (value: number) => `${Math.round((value / total) * 100)}%`;
  return (
    <figure className="sp-chart">
      <ol className="sp-bars" aria-hidden="true">
        {head.map((row, index) => {
          const text = `${row.name}: ${formatStat(row.value, lang)} (${percent(row.value)})`;
          return (
            <li
              key={row.name}
              className="sp-bar"
              style={vars({ "--i": index, "--w": row.value / max })}
              onPointerEnter={(event) => onTip(event.currentTarget, text)}
              onPointerLeave={() => onTip(null)}
            >
              <span className="sp-bar-name">{row.name}</span>
              <span className="sp-bar-track">
                <span className="sp-bar-fill" />
                <span className="sp-bar-value num">{formatStat(row.value, lang)}</span>
              </span>
            </li>
          );
        })}
      </ol>
      <table className="visually-hidden">
        <caption>{t("stats.byRegion")}</caption>
        <tbody>
          {head.map((row) => (
            <tr key={row.name}>
              <th scope="row">{row.name}</th>
              <td>{formatStat(row.value, lang)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

function MonthColumns({
  values,
  year,
  month,
  lang,
  onTip,
}: {
  values: PublicStatisticsCharts["fires_by_month"];
  year: number;
  month: number;
  lang: string;
  onTip: TipHandler;
}) {
  const { t } = useI18n();
  const [active, setActive] = useState<number | null>(null);
  const columnsRef = useRef<HTMLDivElement | null>(null);
  if (!values?.length) return <p className="sp-empty">{t("stats.noData")}</p>;

  const names = monthNames(lang);
  const max = Math.max(...values.map((value) => value ?? 0), 1);
  const peak = values.reduce<number>((best, value, index) => ((value ?? -1) > (values[best] ?? -1) ? index : best), 0);
  const caption = fill(t("stats.byMonth"), { year });
  const tipText = (index: number) => `${names[index]}: ${values[index] ?? "—"}`;

  // klaviatura: grafik bitta tab to'xtashi, oylar ← → bilan almashtiriladi
  const show = (index: number | null) => {
    setActive(index);
    const column = index === null ? null : columnsRef.current?.children[index];
    onTip((column as HTMLElement | null) ?? null, index === null ? undefined : tipText(index));
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = month - 1;
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const step = event.key === "ArrowRight" ? 1 : -1;
      show(Math.max(0, Math.min(last, (active ?? last) + step)));
    }
  };

  return (
    <figure className="sp-chart">
      <div
        className="sp-columns"
        ref={columnsRef}
        tabIndex={0}
        role="img"
        aria-label={`${caption}. ${values.map((_, index) => (values[index] === null ? null : tipText(index))).filter(Boolean).join(", ")}`}
        onKeyDown={onKeyDown}
        onFocus={() => show(active ?? peak)}
        onBlur={() => show(null)}
      >
        {values.map((value, index) => (
          <span
            key={index}
            className={[
              "sp-col",
              value === null ? "is-future" : "",
              index === peak && value ? "is-peak" : "",
              index === active ? "is-active" : "",
            ].join(" ")}
            style={vars({ "--i": index, "--h": (value ?? 0) / max })}
            onPointerEnter={value === null ? undefined : () => show(index)}
            onPointerLeave={() => show(null)}
          >
            <span className="sp-col-fill" />
            {index === peak && value ? <span className="sp-col-label num">{value}</span> : null}
          </span>
        ))}
      </div>
      <div className="sp-axis" aria-hidden="true">
        {names.map((name, index) => (
          <span key={name} className={index % 3 === 0 || index === month - 1 ? undefined : "is-quiet"}>
            {name}
          </span>
        ))}
      </div>
    </figure>
  );
}

function AppealsMeter({ appeals, lang }: { appeals: PublicStatisticsCharts["appeals_year"]; lang: string }) {
  const { t } = useI18n();
  if (!appeals?.total) return <p className="sp-empty">{t("stats.noData")}</p>;
  const open = appeals.total - appeals.closed;
  const closedText = fill(t("stats.closed"), { n: formatStat(appeals.closed, lang) });
  const openText = fill(t("stats.open"), { n: formatStat(open, lang) });
  return (
    <figure className="sp-chart">
      <div className="sp-meter" role="img" aria-label={`${closedText}, ${openText}`}>
        <span className="sp-meter-fill" style={vars({ "--w": appeals.closed / appeals.total })} />
      </div>
      <ul className="sp-legend">
        <li>
          <i className="is-accent" aria-hidden="true" />
          {closedText}
        </li>
        <li>
          <i aria-hidden="true" />
          {openText}
        </li>
      </ul>
    </figure>
  );
}

/* --------------------------------------------------------------- panel */

export function HeroStatsPanel({
  items,
  charts,
}: {
  items: StatisticItem[];
  charts: PublicStatisticsCharts | null;
}) {
  const { t, lang } = useI18n();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [tip, setTip] = useState<Tip | null>(null);

  const byCode = new Map(items.map((item) => [item.code, item]));
  const forest = byCode.get(FOREST);
  const share = byCode.get(SHARE);
  const trees = byCode.get(TREES);
  const fires = byCode.get(FIRES);
  const appeals = byCode.get(APPEALS);
  const extra = items.filter((item) => !CHARTED.has(item.code) && item.value !== null);

  const onTip: TipHandler = (target, text) => {
    const root = rootRef.current;
    if (!target || !root || !text) {
      setTip(null);
      return;
    }
    const mark = target.getBoundingClientRect();
    const box = root.getBoundingClientRect();
    setTip({ text, x: mark.left + mark.width / 2 - box.left, y: mark.top - box.top });
  };

  if (!items.length) return null;

  let order = 0;
  const card = (key: string, content: ReactNode, className = "") => (
    <article className={`sp-card ${className}`} key={key} style={vars({ "--card": order++ })}>
      {content}
    </article>
  );

  return (
    <section
      className="stats-panel"
      ref={rootRef}
      data-reveal
      aria-label={t("stats.title")}
      onPointerLeave={() => setTip(null)}
    >
      <div className="sp-grid">
        {forest
          ? card(
              FOREST,
              <>
                <Figure item={forest} lang={lang} />
                {share ? <Waffle share={share} lang={lang} /> : null}
              </>,
            )
          : null}
        {trees
          ? card(
              TREES,
              <>
                <Figure item={trees} lang={lang} />
                <RegionBars rows={charts?.trees_by_region ?? null} lang={lang} onTip={onTip} />
              </>,
            )
          : null}
        {fires
          ? card(
              FIRES,
              <>
                <Figure item={fires} lang={lang} icon={<Icon name="flame" size={14} />} />
                <MonthColumns
                  values={charts?.fires_by_month ?? null}
                  year={charts?.year ?? new Date().getFullYear()}
                  month={charts?.month ?? new Date().getMonth() + 1}
                  lang={lang}
                  onTip={onTip}
                />
              </>,
              "is-fire",
            )
          : null}
        {appeals
          ? card(
              APPEALS,
              <>
                <Figure item={appeals} lang={lang} />
                <AppealsMeter appeals={charts?.appeals_year ?? null} lang={lang} />
              </>,
            )
          : null}
      </div>

      {extra.length ? (
        <dl className="sp-extra">
          {extra.map((item, index) => {
            const { value, unit } = splitValue(item, lang);
            return (
              <div key={item.code} style={vars({ "--i": index })}>
                <dt>{item.label}</dt>
                <dd className="num">
                  {value}
                  {unit ? <small>{unit}</small> : null}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}

      {tip ? (
        <div className="sp-tip" aria-hidden="true" style={vars({ "--x": `${tip.x}px`, "--y": `${tip.y}px` })}>
          {tip.text}
        </div>
      ) : null}
    </section>
  );
}
